/**
 * Loading spinner component
 * Displays animated loading spinner with optional text
 */

import { useTranslation } from 'react-i18next';

interface LoadingProps {
  size?: 'sm' | 'md' | 'lg';
  text?: string;
  fullScreen?: boolean;
}

export function Loading({
  size = 'md',
  text,
  fullScreen = false,
}: LoadingProps) {
  const { t } = useTranslation();

  const sizeClasses = {
    sm: 'h-6 w-6 border-2',
    md: 'h-12 w-12 border-2',
    lg: 'h-16 w-16 border-4',
  };

  const spinner = (
    <div className="flex flex-col items-center justify-center gap-3">
      <div
        className={`animate-spin rounded-full ${sizeClasses[size]} border-primary-600 border-t-transparent`}
        role="status"
        aria-label={t('app.loading')}
      >
        <span className="sr-only">{t('app.loading')}</span>
      </div>
      {text && (
        <p className="text-sm font-medium text-gray-600" aria-live="polite">
          {text}
        </p>
      )}
    </div>
  );

  if (fullScreen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-90 z-50">
        {spinner}
      </div>
    );
  }

  return <div className="flex items-center justify-center py-8">{spinner}</div>;
}
